import React from 'react'
import { Title, Subtitle, Subsubtitle, Body, Item, Description } from '../components/Common'

const Table = ({children}) =>{
  return(
    <div>
      <table className="min-w-full border text-xs sm:text-base">   
        <tbody>
          {children}
        </tbody>
      </table>
      <br/><br/>
    </div>
  )
}

const Row2 = ({children, title}) =>{
  return(
    <tr>
      <td>{title}</td>
      <td>
        {children}
      </td>
    </tr>
  )
}

export const NotesPage = () => {
  return (
    <div className='w-full min-h-screen flex justify-center bg-stone-200 dark:bg-stone-700'>
        <div className='w-full sm:w-2/3 pt-12 sm:pt-20 pb-4 px-10 sm:px-16 bg-stone-100 dark:bg-stone-600 dark:text-white'>
            <Title>Study Notes Archive</Title>
            <Body>
              This page is my personal repository of notes, insights, and resources accumulated over years of studying Artificial Intelligence.<br/><br/>
              I created it to retain the knowledge I've gained, explaining concepts in the way I understand them best.<br/><br/>
              Whenever I need to revisit these ideas in the future, I don't have to start from scratch, 
              I can just review what I've documented and quickly refresh my understanding
            </Body>

            <Subtitle>Artificial Intelligence</Subtitle>            
            <Table>
              <Row2 title="Linear Regression">
                Output is a continuous value.
              </Row2>
              
              <Row2 title="Logistic Regression">
                Output is a probability (binary class).
              </Row2>

              <Row2 title="Gradient Boosting">
                Strong model composed of weak predictors (usually decision trees):<br/>
                - The initial tree is weak and it predicts the outcome<br/>
                - The next tree, also weak, predicts the error of the previous result in order to correct it.<br/>
                - And so on.<br/>
                It can be used for both linear and logistic regression.<br/>
                It is easily affected by overfitting.<br/>
                It performs very well on tabular data and on small-to-medium-sized datasets.<br/>
                Each tree is "linked" to the previous one because the model is built sequentially.
              </Row2>

              <Row2 title="Random Forest">
                The decision trees are independepent from each other.<br/>
                Each one is trained on a subset of the data.<br/>
                The final prediction is obtained either by averaging (for regression) of by majority voting (for classification).
              </Row2>
            </Table>

            <Table>
              <Row2 title="Normalization">
                Rescale data values to fall within a specific range.<br/>
                Outliers can negatively influence the data's transformation.<br/>
                Used when data features have different scales, as it brings them to a common range.<br/><br/>

                <span className='italic font-extralight'>
                Ex. In KNN, normalization is crucial because, if the features have different scales,
                the feature with the largest range will dominate the distance calculation.
                </span>
              </Row2>

              <Row2 title="Standardization">
                Standardizes the data by setting the mean to 0 and the standard deviation to 1.<br/>
                While outliers can affect the transformation, their impact is generally less significant than in normalization.<br/>
                Ideal when the data is unbounded, contains outliers or when models require a normal distribution.<br/><br/>

                - Numerator: The difference between the value and the mean, indicating how far the value is from the average.<br/>
                - Denominator: The standard deviation, which measures the average distance of the data points from the mean.<br/><br/>
                By dividing the numerator by the standard deviation, the influence of scale is removed, allowing different dataasets to be compared on the same basis.
              </Row2>

              <Row2 title="Regularization">
                Technique that prevents models from becoming too complex, thereby reducing overfitting.
              </Row2>
            </Table>

            <Table>
              <Row2 title="L2 Regularization">
                Adds a penalty term to the Loss function to prevent weights from growing excessively (exploding).<br/>
                The penalty takes into account the weight's value, encouraging the model to keep weights small and more restricted.
              </Row2>

              <Row2 title="Internal Covariance Shift">
                Phenomenon that occurs during model training, when the input's distribution to each layer changes significately.<br/>
                As a result, the weights must continually adapt to these new distributions,<br/>
                making training more difficult since gradients may become very small or very large.
              </Row2>

              <Row2 title="Batch Normalization">
                Normalizes the inputs of a layer per mini-batch.<br/>
                Used to mitigate internal covariate shift.<br/>
                Introduces 2 trainable parameters (gamma and beta), which allow the model to scale and shift the normalized output, effectively 'canceling' the normalization if necessary.
              </Row2>
            </Table>

            <Table>
              <Row2 title="Vanishing Gradient">
                If the value of the gradient is too small, the weight updates in the early layers of the network will be negligible, causing them to remain almost unchanged.<br/><br/>

                <span className='italic font-extralight'>
                Ex. The maximum value of the derivative of the sigmoid is 0.25. If every layer in the network uses the sigmoid activation, then by chain rule,
                the gradient for early layers will be multiplied by 0.25 at each layer, quickly diminishing to a very small value and making weight updated negligible.
                </span>
              </Row2>

              <Row2 title="Residual Network">
                ResNet learns the difference between the input and the desidered output (residual) instead of directly learning the full output.<br/>
                The architecture mitigated the vanishing gradient problem, making it possible to train much deeper networks.<br/><br/>
                
                By adding the input with the output of a resitual block, the model is encouraged to learn only the differences.<br/>
                In the worst case, it can easily default to learn the identity function if the deeper layers start to degrade performance.<br/><br/>

                With skip connections, the derative through the identity path is 1, ensuring that the gradient can flow unchanged to earlier layers.<br/>
              </Row2>
            </Table>

            <Table>
              <Row2 title="Stochastic Gradient Descent (SGD)">
                Stochastic because it uses randomly selected mini-batches instead of the entire dataset.<br/>
                It uses a constant learning rate and does not retain any past gradient information ("doesn't store memory of the past"), 
                meaning each update id indepentent of the previous one.<br/><br/>
                
                TODO: Add Formula
              </Row2>

              <Row2 title="Adaptive Moment Estimation (ADAM)">
                Improvements over basic SGD:<br/>
                - Momentum: Store the direction of the previous gradients.<br/>
                - RMSProp: Adapts the learning rate for each parameter using the moving average of squared gradients.<br/>
                The momentum is divided by the square root of the variance , which helps adjust the step size for parameters with different scales.<br/><br/>

                TODO: Add Formula
              </Row2>
            </Table>

            <Table>
              <Row2 title="Principal Component Analysis (PCA)">
                Reduces high-dimensional data to a lower-dimensional space while retaining as much information as possibile.<br/>
                It is particularly useful for visualization in 2D or 3D.<br/><br/>

                Algorithm: Standardization -&gt; Covariance Matrix -&gt; Eigenvalues &amp; Eigenvectors -&gt; 
                Select the EigenVectors with the largest eigenvalues (principal components). -&gt; Project the data on those vectors.<br/><br/>
                
                In a dataset, some attributes may be highly correlated, meaning they carry similar information. 
                This redundancy can be reduced, which is why PCA focuses on preserving variance, because it represents the most meaningful and informative aspects of the data.
              </Row2>

              <Row2 title="Covariance">
                Measures the relationship between two variables:<br/>
                - If positive: when one increases, the other tends to increase.<br/>
                - If negative: when one variable increases, the other tends to decrease (and viceversa).<br/>
                - If near zero: there is little to no linear relationship between the two variable (positive and negative devations cancel out) -&gt; Random
              </Row2>

              <Row2 title="Determinant">
                - Represent how the area or volume is scaled (increases, decreases or collapses) after a linear transformation.<br/>
                - If the determinant of a matric is zero, the matrix is not invertible. This also means that its column vectors are
                linearly dependent, meaning at least one of them can be expressed as a linear combination of the others.
              </Row2>

              <Row2 title="Eigenvalues">
                - Quantify how much a corresponding eigenvector is stretched or compressed by a linear transformation.<br/>
                - In PCA, they indicate the variance captured by each principal component.
              </Row2>

              <Row2 title="Eigenvectors">
                - Directions that remain unchanged after a linear transformation.<br/>
                - in PCA, they represent the directions along which the data varies the most.
              </Row2>

            </Table>

            <Table>
              <Row2 title="Face Recognition">
                Traditional face recognition models were trained on a fixed set of classes.<br/>
                But what happens when a new face doesn’t belong to any of them?<br/>
                What if we want to dynamically add a new identity?<br/>
                Do we need to retrain the entire model?
              </Row2>

              <Row2 title="Triplet Loss">
                Margin is added in the formula:<br/>
                - If the negative is too close, the loss is positive, forcing the model to increase their separation.<br/>
                - If the negative is already far enough, the loss becomes zero, and no update is applied.<br/><br/>

                This prevents negatives from being pushed infinitely far apart while ensuring a minimum margin of separation.<br/><br/>

                TODO: Add Formula
              </Row2>

            <Row2 title="ArcNet">
                Addresses the limitations of Triplet Loss, whose main drawback is the need for hard sample mining,
                which makes training computationally expensive.<br/>
                Instead of relying on triplets, ArcFace uses a classification-based approach with an additive angular margin,
                enforcing better separation between classes on a hypersphere.
              </Row2>
            </Table>

            <Table>
              <Row2 title="InceptionNet">
                Uses multiple kernels of different sizes in parallel, then it concatenates their output along the channel dimension:<br/>
                - 1x1: Reduce channel depth and computational cost.<br/>
                - 3x3, 5x5: Capture feature at different spatial scales.<br/>
                - Max pooling 3x3: Improves robustness to small translations.<br/><br/>
                To ensure concatenation is possible, padding is applied so that all branches output feature maps with the same spatial resolution.
                
              </Row2>

              <Row2 title="U-Net">
                Model with the arichitectural structure similar to a U, used for image segmentation:<br/>
                  - Encoder:<br/>
                    * 3x3: Detect features, preserve spatial resolution, and increase the number of channels.<br/>
                    * Max pooling: Reduces the spatial resolution, increases the receptive field and improves efficiency.<br/>
                  - Decoder:<br/>
                    * Up-convolution: Increases spatial resolution and decrease channel depth.<br/>
                    * Skip connection: Concatenate encoder outputs with decoder features to recover spatial information.<br/>
                  - Output:<br/>
                    * 1x1: Assign each pixel the probabilty of belonging to a class.<br/>  
                  
              </Row2>

            <Row2 title="Up-Convolution">
                Zeroes are inserted between the input-pixels (zero-insertion), then a convolution kernel is applied.
                This process expands the spatial resolution of the feature map, producing an output generally larger than the input.
                Additionally padding and stride settings control the exact output size.<br/><br/>

                <span className='italic font-extralight'>
                  "Convolution adds up the information spread in various pixels to one pixel, whereas up-convolution spread the information present in one pixel to
                  various pixels"
                </span>
              </Row2>
            </Table>

        </div>
    </div>
  )
}
